/**
 * Publieke embeddable Ebook Bibliotheek pagina.
 * Flow: bezoeker vult naam + email (verplicht) + telefoon + bedrijf (optioneel) in.
 * Backend maakt een Lead aan (source=ebook_library) + access grant met token.
 * Token wordt in localStorage bewaard zodat bezoeker bij volgend bezoek direct
 * de bibliotheek ziet (zonder opnieuw te hoeven invullen).
 * Per ebook:
 *   - "Lees" → opent een ingesloten PDF-lezer modal (iframe + toolbar).
 *   - "Download" → fetcht de PDF als blob en triggert een echte browser download
 *     (werkt ook binnen iframe embeds en met cross-origin backend).
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ebookLibraryAPI, API_URL } from '@/lib/api';
import {
  BookOpen, Mail, User, Building2, Phone, Loader2, ExternalLink,
  Download, Search, ShoppingCart, Library as LibraryIcon, AlertCircle,
  CheckCircle2, X, RotateCcw, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import BookingCta from '@/components/BookingCta';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';
const STORAGE_KEY = 'ebook_library_grant_v1';
// Gebruik lokale PDF.js worker uit public folder. Gebruik .js i.p.v. .mjs omdat
// Hostinger/custom domains .mjs soms als text/plain serveren, waardoor module import faalt.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';



function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatPrice(price) {
  const n = Number(price || 0);
  if (n === 0) return 'Gratis';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);
}

export default function EbookLibraryPage() {
  // Stap 1 = formulier, Stap 2 = bibliotheek
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [contact, setContact] = useState({ name: '', email: '', phone: '', company: '' });

  // Access state
  const [token, setToken] = useState('');
  const [ebooks, setEbooks] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [bookingCfg, setBookingCfg] = useState(null);

  // UI state
  const [search, setSearch] = useState('');
  const [reader, setReader] = useState(null);          // { id, title } when modal open
  const [downloadingId, setDownloadingId] = useState(null);

  // Force light mode op publieke pagina
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  // Load booking-CTA config (booking_url, label, etc.)
  useEffect(() => {
    ebookLibraryAPI.getConfig?.()
      .then((res) => setBookingCfg(res.data))
      .catch(() => { /* CTA valt terug op defaults */ });
  }, []);

  // On mount: check if we already have a grant
  useEffect(() => {
    (async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const data = JSON.parse(saved);
          if (data?.token) {
            // Validate token by trying to fetch library (we just refresh the public list)
            const res = await ebookLibraryAPI.listPublic();
            const allEbooks = res.data?.ebooks || [];
            // Mark access flags client-side (we trust the saved data has same structure)
            const merged = allEbooks.map(e => {
              const cached = (data.ebooks || []).find(x => x.id === e.id);
              return {
                ...e,
                has_access: cached?.has_access ?? !e.is_paid,
                purchase_required: cached?.purchase_required ?? !!e.is_paid,
              };
            });
            setToken(data.token);
            setContactInfo(data.contact);
            setEbooks(merged);
            setStep(2);
          }
        }
      } catch (e) {
        // niet kritiek
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredEbooks = useMemo(() => {
    if (!search.trim()) return ebooks;
    const s = search.toLowerCase();
    return ebooks.filter(e =>
      (e.title || '').toLowerCase().includes(s) ||
      (e.description || '').toLowerCase().includes(s) ||
      (e.author || '').toLowerCase().includes(s)
    );
  }, [ebooks, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const name = contact.name.trim();
    const email = contact.email.trim();
    if (!name) return setError('Vul je naam in.');
    if (!email) return setError('Vul je e-mailadres in.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Ongeldig e-mailadres.');

    setSubmitting(true);
    try {
      const res = await ebookLibraryAPI.requestAccess({
        contact: {
          name,
          email,
          phone: contact.phone.trim(),
          company: contact.company.trim(),
        },
      });
      const data = res.data || {};
      setToken(data.token);
      setEbooks(data.ebooks || []);
      setContactInfo({
        name,
        email,
        phone: contact.phone.trim(),
        company: contact.company.trim(),
      });

      // Bewaar in localStorage zodat we niet opnieuw hoeven te vragen
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          token: data.token,
          contact: { name, email, phone: contact.phone.trim(), company: contact.company.trim() },
          ebooks: data.ebooks || [],
          ts: Date.now(),
        }));
      } catch (e) { /* ignore quota */ }

      setStep(2);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setEbooks([]);
    setContactInfo(null);
    setContact({ name: '', email: '', phone: '', company: '' });
    setStep(1);
  };

  const apiBase = (API_URL || '').replace(/\/$/, '');

  const handleRead = useCallback((book) => {
    if (!book?.has_access) return;
    setReader({
      id: book.id,
      title: book.title || 'Ebook',
      expectedSize: book.file_size || null,
    });
  }, []);

  const handleDownload = useCallback(async (book) => {
    if (!book?.has_access || !token) return;
    setDownloadingId(book.id);
    try {
      const url = `${apiBase}/api/ebook-library/download/${book.id}?token=${encodeURIComponent(token)}`;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) {
        // Fallback: simulate direct click op anchor (laat browser server-side header doen)
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(book.title || 'ebook').replace(/[^a-zA-Z0-9 _.\-]/g, '')}.pdf`;
        a.target = '_self';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      const blob = await res.blob();
      const contentType = (res.headers.get('content-type') || blob.type || '').toLowerCase();
      const expectedSize = Number(book.file_size || 0);
      if (!contentType.includes('pdf')) {
        throw new Error('Download response is geen PDF. Probeer opnieuw of upload de PDF opnieuw.');
      }
      if (expectedSize && blob.size !== expectedSize) {
        throw new Error(`Downloadgrootte klopt niet (${formatBytes(blob.size)} ontvangen, verwacht ${formatBytes(expectedSize)}).`);
      }
      const safeName = `${(book.title || 'ebook').replace(/[^a-zA-Z0-9 _.\-]/g, '').trim() || 'ebook'}.pdf`;
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Geef de browser even tijd om de download te starten voor we de objectUrl revoken
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1500);
    } catch (e) {
      setError(e?.message || 'Download mislukt.');
      // Last-resort fallback: open in nieuw tabblad zodat de browser/server error zichtbaar is
      try {
        const fallback = `${apiBase}/api/ebook-library/download/${book.id}?token=${encodeURIComponent(token)}`;
        window.open(fallback, '_blank', 'noopener');
      } catch { /* ignore */ }
    } finally {
      setDownloadingId(null);
    }
  }, [apiBase, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-8 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-sm mb-4">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 object-contain" onError={(e) => {
              e.currentTarget.outerHTML = '<div class="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center"><span class="text-white font-bold">Z</span></div>';
            }} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight flex items-center justify-center gap-2">
            <LibraryIcon className="w-7 h-7 sm:w-8 sm:h-8 text-orange-600" /> Ebook Bibliotheek
          </h1>
          <p className="text-slate-600 mt-2 max-w-xl mx-auto">
            Gratis toegang tot onze ebooks. Vul eenmalig het formulier in en lees of download direct.
          </p>
        </div>

        {step === 1 && (
          <ContactForm
            contact={contact}
            setContact={setContact}
            onSubmit={handleSubmit}
            submitting={submitting}
            error={error}
          />
        )}

        {step === 2 && (
          <LibraryView
            ebooks={filteredEbooks}
            allCount={ebooks.length}
            token={token}
            search={search}
            setSearch={setSearch}
            contactInfo={contactInfo}
            onReset={handleReset}
            onRead={handleRead}
            onDownload={handleDownload}
            downloadingId={downloadingId}
            bookingCfg={bookingCfg}
          />
        )}

        <footer className="text-center text-xs text-slate-400 mt-10">
          Powered by <span className="font-semibold text-slate-500">Zynq</span> · Je gegevens worden vertrouwelijk behandeld.
        </footer>
      </div>

      {reader && (
        <PdfReaderModal
          ebookId={reader.id}
          title={reader.title}
          expectedSize={reader.expectedSize}
          token={token}
          apiBase={apiBase}
          onClose={() => setReader(null)}
          onDownload={() => {
            const book = ebooks.find(e => e.id === reader.id);
            if (book) handleDownload(book);
          }}
          downloading={downloadingId === reader.id}
        />
      )}
    </div>
  );
}

function ContactForm({ contact, setContact, onSubmit, submitting, error }) {
  return (
    <Card className="max-w-xl mx-auto shadow-lg border-0 bg-white/90 backdrop-blur-sm">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-5">
          <ShieldCheck className="w-5 h-5 text-orange-600" />
          <h2 className="text-lg font-semibold text-slate-900">Vraag toegang aan</h2>
        </div>
        <p className="text-sm text-slate-600 mb-5">
          Vul je gegevens in en krijg direct toegang tot de volledige bibliotheek.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <User className="w-4 h-4 text-slate-400" /> Naam <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              required
              value={contact.name}
              onChange={(e) => setContact(c => ({ ...c, name: e.target.value }))}
              placeholder="Jouw volledige naam"
              data-testid="ebook-name-input"
              className="h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
              <Mail className="w-4 h-4 text-slate-400" /> E-mailadres <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={contact.email}
              onChange={(e) => setContact(c => ({ ...c, email: e.target.value }))}
              placeholder="jij@bedrijf.nl"
              data-testid="ebook-email-input"
              className="h-11"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-slate-400" /> Telefoonnummer
              </Label>
              <Input
                id="phone"
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact(c => ({ ...c, phone: e.target.value }))}
                placeholder="+31 6 ..."
                data-testid="ebook-phone-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company" className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-slate-400" /> Bedrijf
              </Label>
              <Input
                id="company"
                type="text"
                value={contact.company}
                onChange={(e) => setContact(c => ({ ...c, company: e.target.value }))}
                placeholder="Bedrijfsnaam (optioneel)"
                data-testid="ebook-company-input"
                className="h-11"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-sm"
            data-testid="ebook-submit-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" /> Toegang krijgen
              </>
            )}
          </Button>

          <p className="text-[11px] text-slate-400 text-center mt-2">
            Door op "Toegang krijgen" te klikken ga je akkoord met het ontvangen van relevante informatie.
            We delen je gegevens nooit met derden.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

function LibraryView({ ebooks, allCount, token, search, setSearch, contactInfo, onReset, onRead, onDownload, downloadingId, bookingCfg }) {
  return (
    <div className="space-y-5" data-testid="ebook-library-view">
      {/* Top bar */}
      <Card className="border-0 shadow-sm bg-white/90 backdrop-blur-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>
              Welkom <span className="font-semibold text-slate-900">{contactInfo?.name || ''}</span>!
              Je hebt toegang tot {allCount} ebook{allCount === 1 ? '' : 's'}.
            </span>
          </div>
          <div className="sm:ml-auto flex items-center gap-2 flex-1 sm:flex-none">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="search"
                placeholder="Zoek ebooks…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 bg-white"
                data-testid="ebook-search-input"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="text-slate-500"
              title="Reset toegang"
              data-testid="ebook-reset-btn"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Boek een meeting CTA */}
      <BookingCta config={bookingCfg} variant="orange" />

      {/* Empty state */}
      {ebooks.length === 0 && (
        <Card className="border-0 shadow-sm bg-white/90">
          <CardContent className="p-10 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            {allCount === 0 ? (
              <>
                <p className="text-slate-700 font-medium">Nog geen ebooks beschikbaar.</p>
                <p className="text-sm text-slate-500 mt-1">Kom binnenkort terug!</p>
              </>
            ) : (
              <>
                <p className="text-slate-700 font-medium">Geen ebooks gevonden voor "{search}".</p>
                <p className="text-sm text-slate-500 mt-1">Probeer een ander zoekwoord.</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Grid */}
      {ebooks.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ebooks.map(book => (
            <EbookCard
              key={book.id}
              book={book}
              token={token}
              onRead={onRead}
              onDownload={onDownload}
              downloading={downloadingId === book.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EbookCard({ book, token, onRead, onDownload, downloading }) {
  const apiBase = (API_URL || '').replace(/\/$/, '');
  const coverUrl = book.cover_url
    ? (book.cover_url.startsWith('http') ? book.cover_url : `${apiBase}${book.cover_url}`)
    : null;

  const canAccess = !!book.has_access;
  const isPaid = !!book.is_paid;

  return (
    <Card className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow overflow-hidden group flex flex-col">
      {/* Cover */}
      <div
        className={`relative aspect-[3/4] bg-gradient-to-br from-orange-100 via-amber-50 to-orange-50 overflow-hidden ${canAccess ? 'cursor-pointer' : ''}`}
        onClick={canAccess ? () => onRead(book) : undefined}
        role={canAccess ? 'button' : undefined}
        tabIndex={canAccess ? 0 : undefined}
        onKeyDown={canAccess ? (e) => { if (e.key === 'Enter' || e.key === ' ') onRead(book); } : undefined}
        aria-label={canAccess ? `Lees ${book.title}` : undefined}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={book.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-orange-600 p-4">
            <BookOpen className="w-12 h-12 mb-2 opacity-60" />
            <span className="text-sm font-medium text-center line-clamp-3">{book.title}</span>
          </div>
        )}
        {isPaid ? (
          <Badge className="absolute top-2 right-2 bg-amber-500 hover:bg-amber-500 text-white shadow-sm border-0">
            {formatPrice(book.price)}
          </Badge>
        ) : (
          <Badge className="absolute top-2 right-2 bg-emerald-500 hover:bg-emerald-500 text-white shadow-sm border-0">
            Gratis
          </Badge>
        )}
      </div>

      {/* Body */}
      <CardContent className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-slate-900 line-clamp-2 mb-1" title={book.title}>
          {book.title}
        </h3>
        {book.author && (
          <p className="text-xs text-slate-500 mb-2">door {book.author}</p>
        )}
        {book.description && (
          <p className="text-sm text-slate-600 line-clamp-3 mb-3">{book.description}</p>
        )}

        <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-3 mt-auto">
          {book.pages && <span>{book.pages} pag.</span>}
          {book.pages && book.file_size && <span>·</span>}
          {book.file_size && <span>{formatBytes(book.file_size)}</span>}
        </div>

        {canAccess ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onRead(book)}
              className="flex-1 h-9 border-orange-200 hover:bg-orange-50 hover:text-orange-700 text-orange-700 text-sm"
              data-testid={`ebook-view-${book.id}`}
            >
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Lees
            </Button>
            <Button
              onClick={() => onDownload(book)}
              disabled={downloading}
              className="flex-1 h-9 bg-orange-600 hover:bg-orange-700 text-white text-sm"
              data-testid={`ebook-download-${book.id}`}
            >
              {downloading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5 mr-1.5" />
              )}
              Download
            </Button>
          </div>
        ) : (
          <Button
            disabled
            className="w-full h-9 bg-amber-500 hover:bg-amber-500 text-white text-sm cursor-not-allowed"
            title="Betaalde ebook — checkout binnenkort beschikbaar"
          >
            <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Koop voor {formatPrice(book.price)}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── PDF Reader Modal ─────────────────────────────────────────
// Toont de PDF met react-pdf, geladen via het beveiligde backend view-endpoint.
function PdfReaderModal({ ebookId, title, expectedSize, token, apiBase, onClose, onDownload, downloading }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.05);
  const [error, setError] = useState('');
  const [documentKey, setDocumentKey] = useState(0);

  const directUrl = `${apiBase}/api/ebook-library/view/${ebookId}?token=${encodeURIComponent(token)}`;

  // Memoize file and options props to prevent unnecessary reloads
  const fileConfig = useMemo(() => ({
    url: directUrl,
    httpHeaders: {},
    withCredentials: false,
  }), [directUrl]);

  const pdfOptions = useMemo(() => ({
    disableAutoFetch: false,
    disableStream: false,
    cMapUrl: 'https://unpkg.com/pdfjs-dist@5.4.296/cmaps/',
    cMapPacked: true,
  }), []);

  // Reset document key when ebookId changes to force clean worker restart
  useEffect(() => {
    setDocumentKey((prev) => prev + 1);
    setNumPages(null);
    setPageNumber(1);
    setError('');
  }, [ebookId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setPageNumber((p) => Math.max(1, p - 1));
      if (e.key === 'ArrowRight') setPageNumber((p) => Math.min(numPages || p, p + 1));
    };
    document.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [numPages, onClose]);

  const openInNewTab = () => {
    window.open(directUrl, '_blank', 'noopener,noreferrer');
  };

  const onLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setPageNumber(1);
    setError('');
  };

  const onLoadError = (err) => {
    setError(err?.message || 'Onbekende PDF-fout');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ebook-reader-modal"
    >
      <div
        className="flex-1 flex flex-col mx-auto w-full max-w-5xl bg-white shadow-2xl my-3 sm:my-6 rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <BookOpen className="w-4 h-4 text-orange-600 flex-shrink-0" />
          <h2 className="text-sm font-semibold text-slate-800 truncate flex-1 min-w-0" title={title}>
            {title}
          </h2>

          <div className="hidden sm:flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1 py-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              disabled={pageNumber <= 1}
              title="Vorige pagina"
            >
              Vorige
            </Button>
            <span className="px-2 text-xs text-slate-600 min-w-[76px] text-center">
              {numPages ? `${pageNumber} / ${numPages}` : '—'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))}
              disabled={!numPages || pageNumber >= numPages}
              title="Volgende pagina"
            >
              Volgende
            </Button>
          </div>

          <div className="hidden md:flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1 py-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setScale((s) => Math.max(0.7, Number((s - 0.1).toFixed(2))))}
              title="Uitzoomen"
            >
              −
            </Button>
            <span className="px-1 text-xs text-slate-600 min-w-[42px] text-center">{Math.round(scale * 100)}%</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setScale((s) => Math.min(1.8, Number((s + 0.1).toFixed(2))))}
              title="Inzoomen"
            >
              +
            </Button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={openInNewTab}
            className="h-8 text-slate-600 hover:text-slate-900 hidden sm:inline-flex"
            title="Open in nieuw tabblad"
          >
            <ExternalLink className="w-4 h-4 sm:mr-1" />
            <span className="hidden md:inline">Nieuw tabblad</span>
          </Button>
          <Button
            size="sm"
            onClick={onDownload}
            disabled={downloading}
            className="h-8 bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="ebook-reader-download-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
            ) : (
              <Download className="w-4 h-4 sm:mr-1" />
            )}
            <span className="hidden sm:inline">Download</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
            title="Sluiten (Esc)"
            data-testid="ebook-reader-close-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 relative bg-slate-100 min-h-0 overflow-auto p-3 sm:p-5">
          {error && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {error ? (
            <div className="min-h-full flex flex-col items-center justify-center gap-2 p-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
              <p className="text-slate-700 font-medium">PDF kon niet worden geladen.</p>
              <p className="text-sm text-slate-500 max-w-md">{error}</p>
            </div>
          ) : (
            <Document
              key={`${ebookId}-${documentKey}`}
              file={fileConfig}
              onLoadSuccess={onLoadSuccess}
              onLoadError={onLoadError}
              options={pdfOptions}
              loading={(
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                  <span className="text-sm">PDF laden vanuit MongoDB…</span>
                </div>
              )}
              error={(
                <div className="min-h-full flex flex-col items-center justify-center gap-2 p-6 text-center">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                  <p className="text-slate-700 font-medium">PDF kon niet worden weergegeven.</p>
                </div>
              )}
              className="flex justify-center"
              data-testid="ebook-reader-document"
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderAnnotationLayer
                renderTextLayer
                className="shadow-xl bg-white"
                data-testid="ebook-reader-page"
              />
            </Document>
          )}
        </div>
      </div>
    </div>
  );
}
